import React, { useState, useRef, useCallback, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './AddEmployeeModal.module.css';
import { FaUserPlus, FaTimes, FaSave } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { EMPLOYEE_ROLE_OPTIONS } from '../../constants/employeeGroups';
import {
  PROFESSION_OPTIONS,
  formatLanguageInfo
} from '../../constants/employeeOptions';
import LanguageInfoField from './LanguageInfoField';

const workTypeOptions = [
  'Full Time',
  'Part Time'
];

const educationLevelOptions = [
  'Ortaokul / Ortaöğretim',
  'Lise',
  'Ön Lisans (2 Yıllık Üniversite)',
  'Lisans (4 Yıllık Üniversite)',
  'Yüksek Lisans (Master)',
  'Doktora (PhD)'
];

const statusOptions = [
  { value: 1, label: 'Aktif' },
  { value: 0, label: 'İnaktif' }
];

const yesNoOptions = [
  { value: 'Yes', label: 'Evet' },
  { value: 'No', label: 'Hayır' }
];

const nationalityOptions = [
  'Türk',
  'Afganlı',
  'Alman',
  'Amerikalı',
  'Arap',
  'Arnavut',
  'Azeri',
  'Belçikalı',
  'Boşnak',
  'Bulgar',
  'Çinli',
  'Fransız',
  'Gürcü',
  'Hintli',
  'Hollandalı',
  'İngiliz',
  'İranlı',
  'İspanyol',
  'İsveçli',
  'İtalyan',
  'Japon',
  'Kazak',
  'Kırgız',
  'Koreli',
  'Lübnanlı',
  'Makedon',
  'Mısırlı',
  'Moldovalı',
  'Özbek',
  'Pakistanlı',
  'Polonyalı',
  'Portekizli',
  'Romanyalı',
  'Rus',
  'Sırp',
  'Suriyeli',
  'Tacik',
  'Tunuslu',
  'Türkmen',
  'Ukraynalı',
  'Ürdünlü',
  'Yunan',
  'Diğer'
];

const AddEmployeeModal = ({ onClose, onUpdate }) => {
  const { isDarkMode } = useTheme();
  const modalRef = useRef(null);
  const modalBodyRef = useRef(null);
  const [formData, setFormData] = useState({
    eName: '',
    eSurname: '',
    Active: 1,
    address: '',
    annualPermit: 14,
    birthday: '',
    departman: '',
    eMail: '',
    isHandicappedPerson: "No",
    isIntern: "No",
    isRetired: "No",
    languageInfo: '',
    nationality: 'Türk',
    phone: '',
    emergencyPhone: '', // Acil durum telefonu
    profession: '',
    educationInfo: '',
    startDate: '',
    tcNumber: '',
    workType: '',
    authorization: '',
    role: ''
  });

  // Dil bilgisi editörünün çalışma listesi; kayıt formatına (string) çevrimi
  // aşağıdaki effect formData.languageInfo üzerinden yapar
  const [languageEntries, setLanguageEntries] = useState([]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      languageInfo: formatLanguageInfo(languageEntries)
    }));
  }, [languageEntries]);

  // Add state for departments and authorization roles with loading states
  const [departments, setDepartments] = useState([]);
  const [authorizationRoles, setAuthorizationRoles] = useState([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [isLoadingAuthorizationRoles, setIsLoadingAuthorizationRoles] = useState(true);

  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredAuthorizations, setFilteredAuthorizations] = useState([]);

  // Fetch departments and authorization roles from Firestore
  useEffect(() => {
    fetchDepartments();
    fetchAuthorizationRoles();
  }, []);

  // Fetch departments from Firestore
  const fetchDepartments = async () => {
    try {
      setIsLoadingDepartments(true);
      const employeeDepartmentsRef = collection(db, 'DataEntryValues', 'departments', 'EmployeeDepartments');
      const q = query(employeeDepartmentsRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedDepartments = querySnapshot.docs.map(doc => doc.data().value);
      setDepartments(fetchedDepartments);
      setFilteredDepartments(fetchedDepartments);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Departmanlar yüklenirken bir hata oluştu.');
    } finally {
      setIsLoadingDepartments(false);
    }
  };

  // Fetch authorization roles from Firestore
  const fetchAuthorizationRoles = async () => {
    try {
      setIsLoadingAuthorizationRoles(true);
      const authorizationRolesRef = collection(db, 'DataEntryValues', 'roles', 'AuthorizationRoles');
      const q = query(authorizationRolesRef, orderBy('id', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedRoles = querySnapshot.docs.map(doc => doc.data().value);
      setAuthorizationRoles(fetchedRoles);
      setFilteredAuthorizations(fetchedRoles);
    } catch (error) {
      console.error('Error fetching authorization roles:', error);
      toast.error('Yetkilendirme rolleri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoadingAuthorizationRoles(false);
    }
  };

  // Close modal when clicking outside
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  }, [onClose]);
  
  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked ? "Yes" : "No"
      }));
    } else {
      // Convert name and surname to uppercase with Turkish locale support
      const processedValue = (name === 'eName' || name === 'eSurname') 
        ? value.toLocaleUpperCase('tr-TR') 
        : value;
      
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  // Phone input helpers: keep digits only and format as 5XX XXX XX XX
  const normalizePhoneDigits = (input) => {
    const digits = (input || '').replace(/\D/g, '');
    // Optional: if starts with 0, drop it (e.g., 05xx -> 5xx)
    const normalized = digits.startsWith('0') ? digits.slice(1) : digits;
    // Limit to 10 digits total (Turkish mobile numbers without leading 0)
    return normalized.slice(0, 10);
  };

  const formatPhonePretty = (digits) => {
    // Expect up to 10 digits, shown as 5XX XXX XX XX
    const d = digits;
    if (!d) return '';
    const p1 = d.slice(0, 3);
    const p2 = d.slice(3, 6);
    const p3 = d.slice(6, 8);
    const p4 = d.slice(8, 10);
    let out = '';
    if (p1) out += p1;
    if (p2) out += (out ? ' ' : '') + p2;
    if (p3) out += (out ? ' ' : '') + p3;
    if (p4) out += (out ? ' ' : '') + p4;
    return out;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    const digits = normalizePhoneDigits(raw);
    const pretty = formatPhonePretty(digits);
    setFormData(prev => ({ ...prev, phone: pretty }));
  };

  const handlePhonePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    const digits = normalizePhoneDigits(pasted);
    const pretty = formatPhonePretty(digits);
    e.preventDefault();
    setFormData(prev => ({ ...prev, phone: pretty }));
  };
  
  // Emergency phone handlers
  const handleEmergencyPhoneChange = (e) => {
    const raw = e.target.value;
    const digits = normalizePhoneDigits(raw);
    const pretty = formatPhonePretty(digits);
    setFormData(prev => ({ ...prev, emergencyPhone: pretty }));
  };

  const handleEmergencyPhonePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    const digits = normalizePhoneDigits(pasted);
    const pretty = formatPhonePretty(digits);
    e.preventDefault();
    setFormData(prev => ({ ...prev, emergencyPhone: pretty }));
  };

  const handleFilter = (e, type) => {
    const searchTerm = e.target.value.toLowerCase();
    if (type === 'departman') {
      const filtered = departments.filter(option => 
        option.toLowerCase().includes(searchTerm)
      );
      setFilteredDepartments(filtered);
    } else if (type === 'authorization') {
      const filtered = authorizationRoles.filter(option => 
        option.toLowerCase().includes(searchTerm)
      );
      setFilteredAuthorizations(filtered);
    }
    handleChange(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check if email already exists (only if email is provided)
      if (formData.eMail && formData.eMail.trim() !== '') {
        const emailQuery = query(
          collection(db, 'Employees'),
          where('eMail', '==', formData.eMail.trim())
        );
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          toast.error('Bu e-posta adresi ile kayıtlı bir çalışan zaten mevcut!');
          return;
        }
      }

      // Convert boolean strings to actual boolean values
      const formattedData = {
        ...formData,
        isHandicappedPerson: formData.isHandicappedPerson === "Yes",
        isIntern: formData.isIntern === "Yes",
        isRetired: formData.isRetired === "Yes",
      };

      await addDoc(collection(db, 'Employees'), formattedData);
      toast.success('Çalışan başarıyla eklendi!');
      onUpdate(); // Refresh the employee list
      onClose();
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error('Çalışan eklenirken bir hata oluştu!');
    }
  };

  return (
    <div 
      className={styles.modalOverlay} 
      ref={modalRef}
      onClick={handleOverlayClick}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserPlus className={styles.modalIcon} />
            Yeni Çalışan Ekle
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody} ref={modalBodyRef}>
            <div className={styles.formGrid}>
              {/* Personal Information */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>İsim:</label>
                <input
                  type="text"
                  name="eName"
                  value={formData.eName}
                  onChange={handleChange}
                  required
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Soyisim:</label>
                <input
                  type="text"
                  name="eSurname"
                  value={formData.eSurname}
                  onChange={handleChange}
                  required
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>E-posta:</label>
                <input
                  type="email"
                  name="eMail"
                  value={formData.eMail}
                  onChange={handleChange}
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>TC Kimlik No:</label>
                <input
                  type="text"
                  name="tcNumber"
                  value={formData.tcNumber}
                  onChange={handleChange}
                  required
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Uyruk:</label>
                <select
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  {nationalityOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Telefon:</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onPaste={handlePhonePaste}
                  inputMode="numeric"
                  pattern="^[0-9 ]{10,13}$"
                  placeholder="5XX XXX XX XX"
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Acil Durum Telefonu:</label>
                <input
                  type="tel"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleEmergencyPhoneChange}
                  onPaste={handleEmergencyPhonePaste}
                  inputMode="numeric"
                  pattern="^[0-9 ]{10,13}$"
                  placeholder="5XX XXX XX XX (Opsiyonel)"
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Doğum Tarihi:</label>
                <input
                  type="date"
                  name="birthday"
                  value={formData.birthday}
                  onChange={handleChange}
                  className={styles.formInput}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Başlangıç Tarihi:</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Yıllık İzin (gün):</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  name="annualPermit"
                  value={formData.annualPermit}
                  onChange={handleChange}
                  className={styles.formInput}
                  placeholder="14"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Departman:</label>
                <select
                  name="departman"
                  value={formData.departman}
                  onChange={handleChange}
                  className={styles.formSelect}
                  disabled={isLoadingDepartments}
                >
                  <option value="">Seçiniz</option>
                  {departments.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {isLoadingDepartments && (
                  <div className={styles.loadingIndicator}>Departmanlar yükleniyor...</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Yetki:</label>
                <select
                  name="authorization"
                  value={formData.authorization}
                  onChange={handleChange}
                  className={styles.formSelect}
                  disabled={isLoadingAuthorizationRoles}
                >
                  <option value="">Seçiniz</option>
                  {authorizationRoles.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {isLoadingAuthorizationRoles && (
                  <div className={styles.loadingIndicator}>Roller yükleniyor...</div>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Durum:</label>
                <select
                  name="Active"
                  value={formData.Active}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Çalışma Şekli:</label>
                <select
                  name="workType"
                  value={formData.workType}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Seçiniz</option>
                  {workTypeOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Meslek:</label>
                <select
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Seçiniz</option>
                  {PROFESSION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Eğitim Bilgisi:</label>
                <select
                  name="educationInfo"
                  value={formData.educationInfo}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Seçiniz</option>
                  {educationLevelOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Dil Bilgisi:</label>
                <LanguageInfoField
                  entries={languageEntries}
                  onChange={setLanguageEntries}
                  styles={styles}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Çalışan Rolü:</label>
                {/* Rol, eğitim/SOP/form atamalarındaki grup adlarıyla birebir
                    eşleşmek zorunda olduğundan serbest metin yerine listeden seçilir */}
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="">Seçiniz</option>
                  {EMPLOYEE_ROLE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Adres:</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={styles.formInput}
                rows="3"
              ></textarea>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '1rem' }}>
              <div className={styles.formCheckboxGroup}>
                <input
                  type="checkbox"
                  id="isHandicappedPerson"
                  name="isHandicappedPerson"
                  checked={formData.isHandicappedPerson === "Yes"}
                  onChange={handleChange}
                  className={styles.formCheckbox}
                />
                <label htmlFor="isHandicappedPerson" className={styles.formLabel}>
                  Engelli
                </label>
              </div>
              
              <div className={styles.formCheckboxGroup}>
                <input
                  type="checkbox"
                  id="isRetired"
                  name="isRetired"
                  checked={formData.isRetired === "Yes"}
                  onChange={handleChange}
                  className={styles.formCheckbox}
                />
                <label htmlFor="isRetired" className={styles.formLabel}>
                  Emekli
                </label>
              </div>
              
              <div className={styles.formCheckboxGroup}>
                <input
                  type="checkbox"
                  id="isIntern"
                  name="isIntern"
                  checked={formData.isIntern === "Yes"}
                  onChange={handleChange}
                  className={styles.formCheckbox}
                />
                <label htmlFor="isIntern" className={styles.formLabel}>
                  Stajyer
                </label>
              </div>
            </div>
          </div>
          
          <div className={styles.formActions}>
            <button 
              type="button" 
              onClick={onClose} 
              className={styles.buttonCancel}
            >
              İptal
            </button>
            <button 
              type="submit" 
              className={styles.buttonSave}
            >
              <FaSave className={styles.buttonIcon} /> Çalışanı Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;